import React from 'react';

import CostCenterItemSelect from './CostCenterItemSelect';
import ProjectSelect from './ProjectSelect';
import SelectInput from './SelectInput';
import { Account, CostCenterItem, Project } from './Types';
import { useScopeKeys, scopedTree } from './Scope';

// A location being picked: cost-centre item, then project, then account. Any part may be
// unset while picking; the three selects cascade — choosing a level clears the ones below.
export type LocationValue = {
    cost_center_item_id: string | null,
    project_id: string | null,
    account: number | null,
}

export const EMPTY_LOCATION: LocationValue = { cost_center_item_id: null, project_id: null, account: null }

// The three cascading selects — cost-centre → project → account — used across the app (the
// attest invoice rows, the Swish mappings). The enable/disable cascade (project disabled
// until a cost-centre is chosen, account until a project is) and the required/label handling
// live here once. The caller supplies how a project exposes its account numbers and how to
// name an account, so the same component serves both expense (attest) and income (Swish)
// locations. It renders three sibling elements (a fragment), so it drops straight into a grid.
function LocationSelect<P extends Project>({
    costCenterItems, accountNumbersOf, accountName,
    value, onChange,
    required = true, disabled = false, labelsEnabled = true,
    pickableKeys,
}: {
    costCenterItems: CostCenterItem<P>[],
    accountNumbersOf: (project: P) => number[],
    accountName: (accountNumber: number) => string,
    value: LocationValue,
    onChange: (next: LocationValue) => void,
    required?: boolean,
    disabled?: boolean,
    labelsEnabled?: boolean,
    // Overrides the active role's scope as the set of selectable (cost-centre:project) keys — used
    // in expense "prepare" mode to offer any pair that has a potential attestant.
    pickableKeys?: Set<string>,
}) {
    // Value resolution + account options use the FULL tree, so the currently-selected location
    // always renders (even one outside scope, e.g. a mis-categorised draft you're about to fix).
    const costCenterItem = costCenterItems.find(c => c.cost_center_item_id === value.cost_center_item_id) ?? null
    const project = costCenterItem?.projects.find(p => p.project_id === value.project_id) ?? null
    const accountOptions: Account[] = (project ? accountNumbersOf(project) : []).map(
        number => ({ account_number: number, account_name: accountName(number) }))
    // Pickable options: an explicit pickableKeys set (prepare mode) overrides the active role's
    // scope; otherwise the scope (null ⇒ no provider ⇒ no filter). A cost-centre shows only if it
    // has at least one pickable project, and only pickable projects show.
    const scopeKeys = useScopeKeys()
    const keys = pickableKeys ?? scopeKeys
    const scopedItems = scopedTree(costCenterItems, keys, value.cost_center_item_id, value.project_id)
    const scopedCostCenterItem = scopedItems.find(c => c.cost_center_item_id === value.cost_center_item_id) ?? null
    return (
        <>
            <CostCenterItemSelect
                costCenterItems={scopedItems} value={value.cost_center_item_id}
                required={required} disabled={disabled}
                label={labelsEnabled ? 'Kostnadsställe' : undefined}
                onChange={id => onChange({ cost_center_item_id: id, project_id: null, account: null })}/>
            <ProjectSelect
                costCenterItem={scopedCostCenterItem} value={value.project_id}
                required={required} disabled={disabled || value.cost_center_item_id === null}
                label={labelsEnabled ? 'Projekt' : undefined}
                onChange={id => onChange({ ...value, project_id: id, account: null })}/>
            <SelectInput<number, Account, never>
                options={accountOptions} valueGetter={a => a.account_number} nameGetter={a => a.account_name}
                value={value.account} required={required} disabled={disabled || value.project_id === null}
                label={labelsEnabled ? 'Konto' : undefined}
                onChange={number => onChange({ ...value, account: number })}/>
        </>
    )
}

export default LocationSelect
